import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { CheckCircle, Trash3, Eye } from "react-bootstrap-icons";
import Button from "react-bootstrap/Button";
import Skeleton from "react-loading-skeleton";
import { useApi } from "../../Controller/ApiController";
import { useDateFormat } from "../../Util/InputDateFormate";
import { DateFormatWithStartOFTheDay } from "../../Util/DateFormat";

interface Notification {
  id: number;
  compareStatus: boolean;
  data: {
    message: string;
    email: string;
    mobile_number: number;
    visitorId: number;
  };
  created_at: string;
  read_at: Date | null;
}

interface AllNotificationsProps {
  onMarkAsRead: (notificationId: number) => void;
  onDelete: (notificationId: number) => void;
  viewNotificationData: (message: any) => void;
  unreadNotification?: () => void;
}

const AllNotifications = forwardRef<unknown, AllNotificationsProps>(
  (
    { onMarkAsRead, onDelete, viewNotificationData, unreadNotification },
    ref,
  ) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const dateFormat = useDateFormat();
    const { apiGet, apiPost, apiDelete } = useApi();

    const fetchNotifications = async (page = 1) => {
      try {
        setIsLoading(true);
        const response = await apiPost(
          `notifications/search?page=${page}&limit=10`,
          {
            sort: [{ field: "created_at", direction: "desc" }],
          },
        );

        const fetchedNotifications: Notification[] = response.data;

        setNotifications((prevNotifications) =>
          page === 1
            ? fetchedNotifications
            : [...prevNotifications, ...fetchedNotifications],
        );
        setTotalCount(response.meta.total);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };
    useEffect(() => {
      setHasMore(notifications.length < totalCount ? true : false);
    }, [notifications, totalCount]);

    useEffect(() => {
      fetchNotifications();
      // eslint-disable-next-line
    }, []);
    useImperativeHandle(ref, () => ({
      refreshNotifications: () => fetchNotifications(1),
    }));

    const handleMarkNotificationAsRead = async (notificationId: number) => {
      try {
        await apiGet(`notification/read/${notificationId}`);
        onMarkAsRead(notificationId);
        fetchNotifications();
        unreadNotification?.();
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    };

    const handleDeleteNotification = async (notificationId: number) => {
      try {
        await apiDelete(`notifications/${notificationId}`);
        onDelete(notificationId);
        setNotifications((prevNotifications) =>
          prevNotifications.filter(
            (notification) => notification.id !== notificationId,
          ),
        );
        fetchNotifications();
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    };

    const loadMoreNotifications = () => {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchNotifications(nextPage);
    };
    function formatMessage(message: string) {
      const snakeRegex = /^([a-z_]+)\s/;
      const match = message.match(snakeRegex);

      if (match) {
        const formatted = match[1]
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return message.replace(snakeRegex, `${formatted} `);
      }

      return message;
    }
    return (
      <div className="p-0">
        {isLoading && currentPage === 1 ? (
          <Skeleton count={8} height={55} />
        ) : notifications.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted fs-5">No data available.</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`d-flex justify-content-between align-items-center p-3 notification-item ${
                  !notification.read_at ? "unread" : ""
                }`}
              >
                <div className="d-flex flex-column">
                  <small className="text-muted">
                    {formatMessage(notification.data.message)}
                  </small>
                  <small>
                    {DateFormatWithStartOFTheDay(
                      notification.created_at,
                      dateFormat?.replace("dd", "DD"),
                    )}
                  </small>
                </div>

                <div className="d-flex align-items-center">
                  {!notification.read_at && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-2"
                      onClick={() =>
                        handleMarkNotificationAsRead(notification.id)
                      }
                    >
                      <CheckCircle size={15} className="me-1" /> Read
                    </Button>
                  )}
                  <Button
                    onClick={() => viewNotificationData(notification.data)}
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                  >
                    <Eye size={15} className="me-1" />
                    View
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteNotification(notification.id)}
                  >
                    <Trash3 size={15} className="me-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}

            {hasMore && !isLoading && (
              <div className="text-center mt-4 mb-5">
                <button
                  className="btn addnew-btn btn-sm"
                  onClick={loadMoreNotifications}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

export default AllNotifications;
